import { createSelector } from 'reselect';
import { State } from 'reducers';
import { Device, DeviceId, DeviceIndex, DeviceMap, Camera, Stream, StreamId, StreamIndex, DeviceIdMap } from 'types';
import { splitStreamId , isStreamId } from 'util/streamId';
import { initialState } from 'reducers/deviceReducer';

export type HasDeviceIndex = {
    deviceIndex: DeviceIndex | null;
};

export type HasDeviceId = {
    deviceId: DeviceId | null;
};

export type HasStreamIndex = {
    streamIndex: StreamIndex | null;
};

export type HasStreamId = {
    streamId: StreamId | null;
};

export const isCamera = (device: Device | null): boolean =>
    !!device && (
        device.deviceType === 'Fixed' ||
        device.deviceType === 'PTZ'
    )
;

export const isAccessDevice = (device: Device | null): boolean =>
    !!device && (
        device.deviceType === 'AccessDevice'
    )
;

const getDeviceIndex = <T extends HasDeviceIndex>(state: State, props: T) => props.deviceIndex;

const getDeviceId = <T extends HasDeviceId>(state: State, props: T) => props.deviceId;

const getStreamId = <T extends HasStreamId>(state: State, props: T) => props.streamId;


const getDeviceSlice = (state: State) => state && state.device || initialState;

export const getDevices =
    createSelector(
        getDeviceSlice,
        device => device.data
    );

export const isLoadingDevices =
    createSelector(
        getDeviceSlice,
        device => device.loading
    );

export const getDevicesError =
    createSelector(
        getDeviceSlice,
        device => device.error
    );

const emptyDeviceIdMap: DeviceIdMap = {};

export const getDeviceIdByDeviceIndex =
    createSelector(
        getDevices,
        devices =>
            devices
                ? Object.keys(devices)
                    .reduce((map, deviceId) => {
                        map[`${devices[deviceId].deviceIndex}`] = deviceId;
                        return map;
                    }, {} as DeviceIdMap)
                : emptyDeviceIdMap
    );

export const getCameraByDeviceIndex =
    createSelector(
        getDevices,
        getDeviceIdByDeviceIndex,
        getDeviceIndex,
        (devices, idByIndex, deviceIndex) => {
            // Bail out if the devices haven't been loaded yet
            if (!devices) return null;

            // Lookup the device ID and then the device
            const deviceId = idByIndex[`${deviceIndex}`];
            const device = devices[deviceId];
            
            // Bail out if the device is not a camera (or doesn't exist)
            if (!isCamera(device)) return null;
            
            return device as Camera;
        }
    );


export const getCamera =
    createSelector(
        getDevices,
        getDeviceId,
        getDeviceIndex,
        getDeviceIdByDeviceIndex,
        (devices, deviceId, deviceIndex, deviceIdByDeviceIndex) => {
            // Bail out if the devices haven't been loaded yet
            if (!devices) return null;

            // Lookup the device ID if we were given the index instead
            if (!deviceId && deviceIndex && deviceIdByDeviceIndex) {
                deviceId = deviceIdByDeviceIndex[`${deviceIndex}`];
            }

            // Bail out if there is no camera specified;
            if (deviceId === null) return null;

            const device = devices[`${deviceId}`];

            // Bail out if the device is not a camera (or doesn't exist)
            if (!isCamera(device)) return null;

            return device as Camera;
        }
    );

// Helper function used in a few places... so extracted out as a dedicated function (not techincally a selector)
export const getStreamFromDevicesAndStreamId = (devices: DeviceMap | null, streamId: StreamId | null): Stream | null => {
    // Bail out if the devices haven't been loaded yet
    if (!devices) return null;

    // Bail out if the stream ID is invalid (should never happen)
    if (!isStreamId(streamId)) return null;

    const { deviceId, streamIndex } = splitStreamId(streamId);
    const device = devices[deviceId];

    // Bail out if the device is not a camera (or doesn't exist)
    if (!isCamera(device)) return null;

    const camera = device as Camera;

    // Bail out if the stream index is invalid
    const stream = camera.streams[streamIndex];
    if (typeof stream !== 'object') return null;

    return stream;
};

// Helper function used in a few places... so extracted out as a dedicated function (not techincally a selector)
export const getCameraFromDevicesAndStreamId = (devices: DeviceMap | null, streamId: StreamId | null): Camera | null => {
    // Bail out if the devices haven't been loaded yet
    if (!devices) return null;

    // Bail out if the stream ID is invalid (should never happen)
    if (!isStreamId(streamId)) return null;

    const { deviceId } = splitStreamId(streamId);
    const device = devices[deviceId];

    // Bail out if the device is not a camera (or doesn't exist)
    if (!isCamera(device)) return null;

    return device as Camera;
};

export const getStream: Reselect.OutputParametricSelector<State, HasStreamId, Stream | null, (dm: DeviceMap, id: StreamId) => Stream | null> =
    createSelector(
        getDevices,
        getStreamId,
        getStreamFromDevicesAndStreamId
    );

export const getCameraFromStreamId: Reselect.OutputParametricSelector<State, HasStreamId, Camera | null, (dm: DeviceMap, id: StreamId) => Camera | null> =
    createSelector(
        getDevices,
        getStreamId,
        getCameraFromDevicesAndStreamId
    );



// WEBPACK FOOTER //
// ./src/selectors/deviceSelectors.ts